/**
 * @fileoverview Grow's frost or freeze warning as a slim bar under the header,
 * on every signed-in page (owner, 2026-10-16: "it should be a slim header bar
 * that shows on all pages - reduce the words so it fits into two lines max").
 * It was a big card at the top of Today alone.
 *
 * The words are the server's: the header answer's alert (GET /header, W24
 * Job D), which is the Today brief's own alert (todo.alert.bar, from the
 * alert's own job, so the bar and the card agree: "Freeze tomorrow night,
 * 25°F: cover plants tomorrow before dark.", or "bring pots in" when the job
 * is a bed of pots'), two lines at most. The bar used to read the whole Today
 * brief on every page for these words; now the small header answer carries
 * them. The bar opens Today at the cover job: tonight's under Today, tomorrow
 * night's under Coming up (the group's id, scrolled to when Today is already
 * open). Nothing shows without an alert, or before the garden has a postcode
 * (the header answer is not read then).
 *
 * Where: Header, under its row, so it stays in view with the header.
 * API: none of its own: the header answer (WeatherContext, GET /header).
 * Words: the server's.
 *
 * @module AlertBar
 */

import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronRight, ThermometerSnowflake } from 'lucide-react';
import { useWeatherContext } from '@/shared/hooks/useWeatherContext';

/**
 * The warning bar; nothing when there is no frost or freeze tonight or tomorrow night.
 *
 * @returns The bar, or nothing.
 */
export const AlertBar: React.FC = () => {
  const { alert } = useWeatherContext();
  const { pathname } = useLocation();
  const text = alert?.bar || alert?.title || null;
  if (!alert || !text) return null;
  /** The group its cover job is in on Today. */
  const groupId = alert.when === 'tomorrow' ? 'todo-coming' : 'todo-today';
  return (
    <Link
      to={{ pathname: '/today', hash: groupId }}
      // Already on Today: to the job's group at once (the router does not scroll to a hash).
      onClick={() => { if (pathname === '/today') document.getElementById(groupId)?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }}
      className="block border-t border-verdict-cold/30 bg-verdict-cold-tint text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
    >
      <span className="container mx-auto flex items-center gap-2 px-4 py-1.5 max-[399px]:px-3">
        <ThermometerSnowflake className="h-4 w-4 shrink-0 text-verdict-cold" aria-hidden="true" />
        <span className="line-clamp-2 min-w-0 flex-1 text-sm font-semibold leading-snug">{text}</span>
        <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
      </span>
    </Link>
  );
};
